import type { FC } from 'react';
import React, { Fragment, useCallback, useState } from 'react';

import { EXTENSION_POINT_BYLINE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { RequireLegacyWRM } from '@confluence/wrm/entry-points/RequireLegacyWRM.ts';
import type { ForgeUIContentBylineItemExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableAfterPaint } from '@confluence/loadable';

import { LivePagesByLineAppsPlaceholder } from './LivePagesByLineAppsPlaceholder.tsx';
import { ByLineAppsComponent } from './ByLineAppsComponent.tsx';

type ByLineAppsProps = {
	contentId: string;
	connectItems: object[];
	forgeItems: ReadonlyArray<ForgeUIContentBylineItemExtensionType>;
	forgeError?: Error;
	isSingleLineByline?: boolean;
	showLivePagesExtensionPoints?: boolean;
	onBylineAppsLoaded?: () => void;
	loadingConnectItems?: boolean;
	loadingForgeItems?: boolean;
	noContainer?: boolean;
};

export const LivePagesByLineApps: FC<ByLineAppsProps> = ({
	contentId,
	connectItems,
	forgeItems,
	forgeError,
	isSingleLineByline,
	onBylineAppsLoaded,
	showLivePagesExtensionPoints,
	loadingConnectItems = false,
	loadingForgeItems = false,
	noContainer = false,
}) => {
	const isLoading = loadingConnectItems || loadingForgeItems;
	const [showLoader, setShowLoader] = useState<boolean>(
		isLoading && !expValEquals('confluence_skip_byline_apps_placeholder', 'isEnabled', true),
	);
	const handleLoad = useCallback(() => {
		setShowLoader(false);
		onBylineAppsLoaded?.();
	}, [onBylineAppsLoaded]);
	const shouldRenderBylineApps = expValEquals(
		'confluence_skip_byline_apps_placeholder',
		'isEnabled',
		true,
	)
		? !isLoading
		: true;

	return (
		<Fragment>
			<ExperienceStart name={EXTENSION_POINT_BYLINE_EXPERIENCE} />
			{showLoader && (
				<LivePagesByLineAppsPlaceholder
					connectItems={connectItems}
					forgeItems={forgeItems}
					noContainer={noContainer}
				/>
			)}
			{shouldRenderBylineApps && (
				<RequireLegacyWRM
					key="byline-app-wrm"
					wrm="wr!com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-content-byline-resources-v5"
				>
					{() => {
						if (forgeError) {
							if (!isErrorMarkedAsHandled(forgeError)) {
								return <ErrorDisplay error={forgeError} />;
							}
							// Process handled errors as successful
							return <ExperienceSuccess name={EXTENSION_POINT_BYLINE_EXPERIENCE} />;
						}

						return (
							<Fragment>
								<ByLineAppsComponent
									// this key is required in order to ensure that a byline app remounts when transitioning to a different page
									// remounting is required to ensure there is no stale data being presented when an app is opened on a second page
									key={contentId}
									contentId={contentId}
									forgeItems={forgeItems}
									connectItems={connectItems}
									showLivePagesExtensionPoints={showLivePagesExtensionPoints}
									onLoad={handleLoad}
									showLoader={showLoader}
									isSingleLineByline={isSingleLineByline}
								/>
								<ExperienceSuccess name={EXTENSION_POINT_BYLINE_EXPERIENCE} />
							</Fragment>
						);
					}}
				</RequireLegacyWRM>
			)}
		</Fragment>
	);
};

export const LazyLivePagesByLineApps: LoadableType<ByLineAppsProps> = LoadableAfterPaint({
	loader: async () => {
		return (
			await import(
				/* webpackChunkName: "loadable-LivePagesByLineApps" */ './LivePagesByLineApps.tsx'
			)
		).LivePagesByLineApps;
	},
	loading: (props) =>
		expValEquals('confluence_skip_byline_apps_placeholder', 'isEnabled', true) ? null : (
			<LivePagesByLineAppsPlaceholder {...props} />
		),
});
